import { Alert, Col, DisplayField, Icon, Row, Spinner, Card } from "@digitools/honest-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TEALIUM_EVENT } from "../../../../../constants/tealiumConstants";
import useTealium from "../../../../../hooks/useTealium";
import checkDate from "../../../utilities/checkDate";
import titleCase from "../../../utilities/titleCase";
import { Link } from "react-router-dom";
import useExperience from "src/hooks/useExperience";
import { Experience } from "src/types/Experience";
import getDisabilityCause from "../../../utilities/getDisabilityCause";
import getDiagnosticCategory from "../../../utilities/getDiagnosticCategory";
import styled from "styled-components";
import StatusBadge, { getStatusColor } from "src/components/Cards/StatusBadge";
import WatchlistCheckBox from "../../WatchlistCheckBox/WatchlistCheckBox";
import CategorizationDocumentUpload from "src/components/documentUpload/CategorizationDocumentUpload";
import useAuthentication from "src/hooks/useAuthentication";
import axios from "axios";
import { saveFile } from "src/utils/pdfUtil";
import { StyledButton } from "src/components/Formatting/StyledComponents";
import { PENDING_STATUS_CODES, APPROVED_STATUS_CODES, DENIED_STATUS_CODES, CLOSED_STATUS_CODES } from '../../../constants/StatusCodes';
import useStatusDataContext from "src/packages/status/hooks/useStatusDataContext";
import getGlossaryTermById from "src/packages/status/utilities/getGlossaryTermById";
import useTranslate from "src/hooks/useTranslate";
import { useTooltipTracking } from 'src/hooks/useTooltipTracking';
import getProductAttributeCode from "src/packages/status/utilities/getProductAttributeCode";
import { useLanguage, useProductAttributeCodes } from "src/packages/status/hooks";
const StatusMessage = styled('div') `
    font-size: 0.9rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
`;
const StyledCol = styled(Col) `
    @media (max-width: 480px){
        display: flex;
        justify-content: space-around;
        flex-direction: column-reverse;
    }
`;
const ClaimInfoCard = ({ claim, associatedLeave, productAttribute, statusDef, claimId, empNum, }) => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { t: newTranslate } = useTranslate();
    const { trackEvent } = useTealium();
    const { experience } = useExperience();
    const [isDownloadError, setIsDownloadError] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const { authHeader } = useAuthentication();
    const { glossary } = useStatusDataContext();
    const { productAttributeCodes } = useProductAttributeCodes();
    const { trackTooltipHover, trackTooltipClick, handleTooltipClose } = useTooltipTracking({
        eventType: TEALIUM_EVENT.EVENT_TYPE.CLAIM_DETAILS
    });
    const glossaryTerms = useMemo(() => {
        return {
            coverage: getGlossaryTermById(glossary, '126'),
            benefitClass: getGlossaryTermById(glossary, '125'),
            disabilityCause: getGlossaryTermById(glossary, '130'),
            diagnosticCategory: getGlossaryTermById(glossary, '129'),
            associatedLeave: getGlossaryTermById(glossary, '5'),
            leaveType: getGlossaryTermById(glossary, '47'),
            leaveReason: getGlossaryTermById(glossary, '44'),
            leaveCategory: getGlossaryTermById(glossary, '38'),
        };
    }, [glossary]);
    const downloadClaimDetails = async () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
            event_name: 'download claim details',
            event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
            event_version: 'claim details'
        });
        try {
            setIsDownloading(true);
            const response = await axios.get(`/status-service/report/${empNum}/${claimId}/claimSummary`, {
                responseType: 'blob',
                headers: { Authorization: authHeader() },
            });
            const reportName = `${claimId}-ClaimDetails.pdf`;
            setIsDownloadError(false);
            setIsDownloading(false);
            saveFile(response, reportName);
        }
        catch (error) {
            setIsDownloadError(true);
            setIsDownloading(false);
        }
    };
    const DownloadClaimSummaryError = () => (<Alert data-testid={'download-claim-details-alert'} type={'error'} closeable={true} onClose={() => setIsDownloadError(false)}>
            <span>
                <strong>Error!</strong> There was a problem downloading the claim details. Please try again later.
            </span>
        </Alert>);
    const category = (claimDiagnosticCategory) => {
        const diagnosticCategory = getDiagnosticCategory(claimDiagnosticCategory);
        if (claimDiagnosticCategory === '35') {
            return diagnosticCategory?.slice(0, 17) + " " + diagnosticCategory?.slice(17);
        }
        else {
            return diagnosticCategory;
        }
    };
    const claimStatusCode = claim.clmSttusCde;
    const isClaimPendingOrApproved = PENDING_STATUS_CODES.includes(claimStatusCode) || APPROVED_STATUS_CODES.includes(claimStatusCode);
    const isStatusDeniedOrClosed = DENIED_STATUS_CODES.includes(claimStatusCode) || CLOSED_STATUS_CODES.includes(claimStatusCode);
    const info = claim.claimPaidLeaveInfo;
    const isPL = !!claim.prodType?.includes("PL");
    const showCauseAndCategory = !isPL;
    const showPaidLeaveFields = isPL && !!info;
    const paidLeaveValue = (attr, code) => getProductAttributeCode(language, productAttributeCodes, 'Leave', attr, code);
    const leaveFields = showPaidLeaveFields
        ? [
            { name: 'leave type', label: 'leaveTypeText', value: paidLeaveValue('Type', info.lveType), tooltip: glossaryTerms.leaveType },
            { name: 'leave reason', label: 'leaveReasonText', value: paidLeaveValue('Reason', info.lveReas), tooltip: glossaryTerms.leaveReason },
            { name: 'leave category', label: 'leaveCategoryText', value: paidLeaveValue('Category', info.lveCatg), tooltip: glossaryTerms.leaveCategory },
        ]
        : [];
    return (<div data-testid={'claimInfoCard'} className='mb-2'>
        <Card heading={`${t('claimDetailsText')} #${claim.clmNum}`} secondaryHeading={<StatusBadge status={productAttribute}>{productAttribute}</StatusBadge>} selectable={false} fontSize='large' elemType='h2' borderColor={getStatusColor(productAttribute)}>
          {statusDef && (<Row className='mb-2'>
              <StatusMessage>
                <Icon size='sm' name='important-message' color='secondary' className='ml-2 mr-2'/>
                {statusDef}
              </StatusMessage>
            </Row>)}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {!isClaimPendingOrApproved && !isStatusDeniedOrClosed && (<DisplayField style={{ flexGrow: '.5', minWidth: '25%' }} label={titleCase(t('statusText').toString())} value={productAttribute}/>)}
            <DisplayField onTooltipOpenHover={trackTooltipHover('coverage')} onTooltipOpenClick={trackTooltipClick('coverage')} onTooltipClose={handleTooltipClose} style={{ flexGrow: '.5', minWidth: isPL ? '15%' : '25%' }} label={titleCase(t('coverageText').toString())} value={claim.prodType} tooltip={newTranslate(glossaryTerms.coverage)}/>
            {leaveFields.map(({ name, label, value, tooltip }) => (<DisplayField key={name} onTooltipOpenHover={trackTooltipHover(name)} onTooltipOpenClick={trackTooltipClick(name)} onTooltipClose={handleTooltipClose} style={{ flexGrow: '.5', minWidth: '25%' }} label={titleCase(t(label).toString())} value={value} tooltip={newTranslate(tooltip)} data-private={true}/>))}
            {experience === Experience.EMPLOYER && claim.classCde && (<DisplayField onTooltipOpenHover={trackTooltipHover('benefit class')} onTooltipOpenClick={trackTooltipClick('benefit class')} onTooltipClose={handleTooltipClose} style={{ flexGrow: '1', maxWidth: '50%' }} label={titleCase(t('benefitClassText').toString())} value={claim.classCde.trim()} tooltip={newTranslate(glossaryTerms.benefitClass)}/>)}
            {claim.clmCloseDte && !isClaimPendingOrApproved && (<DisplayField style={{ flexGrow: '.5', minWidth: '25%' }} label={titleCase(t('closedDateText').toString())} value={checkDate(claim.clmCloseDte)}/>)}
            {experience === Experience.EMPLOYER && showCauseAndCategory && (<DisplayField onTooltipOpenHover={trackTooltipHover('disability cause')} onTooltipOpenClick={trackTooltipClick('disability cause')} onTooltipClose={handleTooltipClose} style={{ flexGrow: '.5', minWidth: '25%' }} label={titleCase(t('disabilityCauseText').toString())} value={getDisabilityCause(claim.clmDisabCause)} tooltip={newTranslate(glossaryTerms.disabilityCause)} data-private={true}/>)}
            {claim.claimDiagnosticCategory && showCauseAndCategory && (<DisplayField onTooltipOpenHover={trackTooltipHover('diagnostic category')} onTooltipOpenClick={trackTooltipClick('diagnostic category')} onTooltipClose={handleTooltipClose} style={{ flexGrow: '.5', minWidth: '25%' }} label={titleCase(t('diagnosticCategoryText').toString())} value={category(claim.claimDiagnosticCategory)} tooltip={newTranslate(glossaryTerms.diagnosticCategory)} data-private={true}/>)}
            <DisplayField onTooltipOpenHover={trackTooltipHover('associated leave')} onTooltipOpenClick={trackTooltipClick('associated leave')} onTooltipClose={handleTooltipClose} style={{ flexGrow: '1' }} label={titleCase(t('associatedLeaveText').toString())} tooltip={newTranslate(glossaryTerms.associatedLeave)} value={associatedLeave ? (<Link data-testid='associatedLeaveLink' to={associatedLeave.to} onClick={() => trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
                event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
                event_name: 'event_no',
                event_results: 'leave',
                event_version: 'details',
            })}>
                    {associatedLeave.lveNum}
                  </Link>) : ('')}/>
          </div>
          {experience === Experience.EMPLOYER && claim.classCde && claim.prodType && claim.prodType.trim() === 'PLNY' && (<div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Row className='mt-2' style={{ marginLeft: 0, width: '100%' }}>
                <DisplayField style={{ flexGrow: '1' }} label={titleCase(t('classDescriptionText').toString())} value={claim.clsDescription}/>
              </Row>
            </div>)}
          <Row className='mt-3'>
            <StyledCol md={6} size={12} sm={12} className={'mb-3'}>
              <CategorizationDocumentUpload eventNum={claim.clmNum} productName={claim.prodType} displayLabel={true} renderAsLink={false} key={'CategorizationDocumentUpload' + claim.clmNum} data-testid='CategorizationDocumentUpload' page='details' isNewCard={true}/>
            </StyledCol>
            {experience === Experience.EMPLOYER && (<>
                <StyledCol md={6} size={12} sm={12} className={'mb-3'}>
                  <StyledButton data-testid={'downloadClaimDetails'} buttonType={'secondary'} size={'medium'} type={'button'} onClick={downloadClaimDetails} disabled={isDownloading} aria-label='Download Claim Details' style={{ textTransform: 'inherit' }}>
                    {isDownloading ? (<Spinner id='claim-info-spinner' size='small' style={{ float: 'left', margin: '0px', marginTop: '-.25rem', marginRight: '.5rem' }}/>) : (<Icon name='download' color={'secondary'} style={{ paddingRight: '.25rem' }}/>)}
                    Download Claim Details
                  </StyledButton>
                </StyledCol>
                <StyledCol md={6} size={12} sm={12} className={'mb-3'}>
                  <WatchlistCheckBox eventId={claimId} eventType={'CLAIM'} isNewCard={true}/>
                </StyledCol>
              </>)}
          </Row>
          {isDownloadError && <DownloadClaimSummaryError />}
        </Card>
      </div>);
};
export default ClaimInfoCard;
